import React, { useState } from 'react';
import { Terminal, Upload, X, Loader2, DollarSign } from 'lucide-react';
import { syncManager } from '../../services/sync';
import { config } from '../../config';

const LogExpenseModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        category: 'General',
        merchant: '',
        description: '',
        amount: '',
        status: 'Paid',
    });
    const [receiptFile, setReceiptFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Form, 2: Upload, 3: Success

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setReceiptFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let receiptUrl = '';

            // 1. Upload Receipt if present
            if (receiptFile) {
                const reader = new FileReader();
                const base64Promise = new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result.split(',')[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(receiptFile);
                });
                const base64Data = await base64Promise;

                const uploadRes = await syncManager.triggerWorkflow('hq-upload-file', {
                    filename: `receipt_${Date.now()}_${receiptFile.name}`,
                    mimeType: receiptFile.type,
                    data: base64Data,
                    folder: 'Expenses'
                });

                if (uploadRes?.webViewLink) {
                    receiptUrl = uploadRes.webViewLink;
                }
            }

            // 2. Log Expense via Workflow
            await syncManager.triggerWorkflow('hq-log-expense', {
                ...formData,
                receiptUrl,
                userId: config.USER_EMAIL || 'Admin'
            });

            setStep(3);
        } catch (error) {
            console.error(error);
            alert('Failed to log expense: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="bg-slate-900 p-6 flex justify-between items-center text-white">
                    <div className="flex items-center gap-3">
                        <div className="bg-rose-500/20 p-2 rounded-xl border border-rose-500/30">
                            <DollarSign size={20} className="text-rose-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black tracking-tight">Log Expense</h2>
                            <p className="text-slate-400 text-xs font-medium">Record business spending</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8">
                    {step === 3 ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Terminal size={32} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 mb-2">Expense Recorded!</h3>
                            <p className="text-slate-500 mb-8">The transaction has been logged to the ledger.</p>
                            <button
                                onClick={onClose}
                                className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-slate-800 transition-all"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        required
                                        value={formData.date}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount (R)</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        placeholder="0.00"
                                        required
                                        step="0.01"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Merchant / Payee</label>
                                <input
                                    type="text"
                                    name="merchant"
                                    placeholder="e.g. Google Cloud, Office Supplies..."
                                    required
                                    value={formData.merchant}
                                    onChange={handleChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                    >
                                        <option>General</option>
                                        <option>Software</option>
                                        <option>Marketing</option>
                                        <option>Contractors</option>
                                        <option>Hosting</option>
                                        <option>Legal</option>
                                        <option>Travel</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Status</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                    >
                                        <option>Paid</option>
                                        <option>Pending</option>
                                        <option>Scheduled</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description (Optional)</label>
                                <textarea
                                    name="description"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Receipt Upload (Optional)</label>
                                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        accept="image/*,application/pdf"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                    />
                                    <Upload className="mx-auto text-slate-400 mb-2" />
                                    <p className="text-xs font-bold text-slate-500">
                                        {receiptFile ? receiptFile.name : "Click to upload receipt"}
                                    </p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? <Loader2 className="animate-spin" /> : <DollarSign size={18} />}
                                {loading ? 'Processing...' : 'Log Expense'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogExpenseModal;
